// Checks whether a destination can be shown in the "Cloaked (iframe)" redirect
// mode: the page must not forbid framing (X-Frame-Options / CSP
// frame-ancestors), or real visitors see an empty screen. POST /api/tools/frame-check.
import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useT } from '../i18n';
import { Button } from './ui/button';
import { Input } from './ui/input';

export default function FrameCheck({ defaultUrl = '' }) {
  const { t } = useT();
  const [url, setUrl] = useState(defaultUrl || '');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = async () => {
    const value = url.trim();
    if (!value) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await axios.post(API + '/tools/frame-check', { url: value });
      setResult(r.data || null);
    } catch (e) {
      const detail = e && e.response && e.response.data ? e.response.data.detail : null;
      setResult({ frameable: null, reason: typeof detail === 'string' ? detail : t('Check failed') });
    } finally {
      setBusy(false);
    }
  };

  let tone = 'text-muted-foreground';
  let verdict = '';
  if (result) {
    if (result.frameable === true) {
      tone = 'text-green-500';
      verdict = t('Can be shown in iframe mode');
    } else if (result.frameable === false) {
      tone = 'text-red-400';
      verdict = t('Blocked: visitors will see an empty screen in iframe mode');
    } else {
      tone = 'text-amber-500';
      verdict = t('Could not confirm: test it on a real phone before launch');
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-border p-3" data-testid="frame-check">
      <p className="text-xs text-muted-foreground">
        {t('Check that the page allows framing. Paste the final offer or landing URL your streams send visitors to.')}
      </p>
      <div className="flex gap-2">
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://"
          data-testid="frame-check-url"
        />
        <Button
          type="button"
          variant="outline"
          onClick={check}
          disabled={busy || !url.trim()}
          data-testid="frame-check-run"
        >
          {busy ? t('Checking…') : t('Check framing')}
        </Button>
      </div>
      {result ? (
        <p className={'text-xs ' + tone} data-testid="frame-check-result">
          {verdict}
          {result.reason ? ' — ' + result.reason : ''}
        </p>
      ) : null}
    </div>
  );
}
